import { Player } from '../player';

export interface TouchControlCallbacks {
  onBreak: () => void;
  onPlace: () => void;
  onInventory: () => void;
  onChat: () => void;
  onCrafting: () => void;
  onHelp: () => void;
  onMute: () => void;
  onPlayerList: () => void;
  onSelectSlot: (slot: number) => void;
}

export class TouchControls {
  private player: Player;
  private callbacks: TouchControlCallbacks;
  private container: HTMLDivElement;

  // Joystick state
  private joystickBase: HTMLDivElement;
  private joystickKnob: HTMLDivElement;
  private joystickTouchId: number | null = null;
  private joystickCenterX = 0;
  private joystickCenterY = 0;
  private moveX = 0;
  private moveZ = 0;
  private isSprinting = false;

  // Camera look joystick
  private lookBase: HTMLDivElement;
  private lookKnob: HTMLDivElement;
  private lookTouchId: number | null = null;
  private lookCenterX = 0;
  private lookCenterY = 0;
  private lookDeltaX = 0;
  private lookDeltaY = 0;

  // Break hold
  private breakInterval: ReturnType<typeof setInterval> | null = null;

  // Animation frame handle
  private rafId = 0;

  // Toolbar mute button reference
  private muteBtn: HTMLDivElement | null = null;

  constructor(player: Player, callbacks: TouchControlCallbacks) {
    this.player = player;
    this.callbacks = callbacks;

    // Create container for all touch controls
    this.container = document.createElement('div');
    this.container.id = 'touch-controls';
    document.body.appendChild(this.container);

    // Create move joystick (left side)
    this.joystickBase = this.createDiv('touch-joystick-base touch-joystick-left');
    this.joystickKnob = this.createDiv('touch-joystick-knob');
    this.joystickBase.appendChild(this.joystickKnob);
    this.container.appendChild(this.joystickBase);

    // Create look joystick (right side)
    this.lookBase = this.createDiv('touch-joystick-base touch-joystick-right');
    this.lookKnob = this.createDiv('touch-joystick-knob');
    this.lookBase.appendChild(this.lookKnob);
    this.container.appendChild(this.lookBase);

    // Create action buttons (bottom center cluster)
    this.createButton('touch-btn-jump', '&#9650;', () => this.triggerJump());
    this.createBreakButton();
    this.createButton('touch-btn-place', '&#9632;', () => callbacks.onPlace());

    // Create top toolbar
    this.createToolbar();

    // Bind touch events on document
    this.bindTouchEvents();
    this.bindHotbarTap();

    // Update loop to feed joystick into player.keys
    this.startUpdateLoop();
  }

  private createToolbar() {
    const toolbar = this.createDiv('touch-toolbar');

    const buttons: Array<{ icon: string; label: string; handler: () => void; className?: string }> = [
      { icon: '&#9783;', label: 'inv', handler: () => this.callbacks.onInventory() },
      { icon: '&#9879;', label: 'craft', handler: () => this.callbacks.onCrafting() },
      { icon: '?', label: 'help', handler: () => this.callbacks.onHelp() },
      { icon: '&#9993;', label: 'chat', handler: () => this.callbacks.onChat() },
      { icon: '&#128101;', label: 'players', handler: () => this.callbacks.onPlayerList() },
      { icon: '&#128266;', label: 'mute', handler: () => this.callbacks.onMute(), className: 'touch-toolbar-mute' },
    ];

    for (const { icon, handler, className } of buttons) {
      const btn = this.createDiv('touch-toolbar-btn' + (className ? ' ' + className : ''));
      btn.innerHTML = icon;
      btn.addEventListener('touchstart', (e) => {
        e.preventDefault();
        e.stopPropagation();
        btn.classList.add('active');
        handler();
      }, { passive: false });
      btn.addEventListener('touchend', (e) => {
        e.preventDefault();
        e.stopPropagation();
        btn.classList.remove('active');
      }, { passive: false });
      btn.addEventListener('touchcancel', () => {
        btn.classList.remove('active');
      });
      toolbar.appendChild(btn);

      if (className === 'touch-toolbar-mute') {
        this.muteBtn = btn;
      }
    }

    this.container.appendChild(toolbar);
  }

  setMuted(muted: boolean) {
    if (this.muteBtn) {
      this.muteBtn.innerHTML = muted ? '&#128263;' : '&#128266;';
      this.muteBtn.classList.toggle('muted', muted);
    }
  }

  private createDiv(className: string): HTMLDivElement {
    const div = document.createElement('div');
    div.className = className;
    return div;
  }

  private createButton(className: string, icon: string, handler: () => void): HTMLDivElement {
    const btn = this.createDiv(className);
    btn.innerHTML = `<span class="touch-btn-icon">${icon}</span>`;
    btn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      e.stopPropagation();
      btn.classList.add('active');
      handler();
    }, { passive: false });
    btn.addEventListener('touchend', (e) => {
      e.preventDefault();
      e.stopPropagation();
      btn.classList.remove('active');
    }, { passive: false });
    btn.addEventListener('touchcancel', (e) => {
      btn.classList.remove('active');
    });
    this.container.appendChild(btn);
    return btn;
  }

  private createBreakButton() {
    const btn = this.createDiv('touch-btn-break');
    btn.innerHTML = '<span class="touch-btn-icon">&#9998;</span>';

    btn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      e.stopPropagation();
      btn.classList.add('active');
      this.callbacks.onBreak();
      this.breakInterval = setInterval(() => this.callbacks.onBreak(), 250);
    }, { passive: false });

    const stopBreak = (e: TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      btn.classList.remove('active');
      if (this.breakInterval) {
        clearInterval(this.breakInterval);
        this.breakInterval = null;
      }
    };
    btn.addEventListener('touchend', stopBreak, { passive: false });
    btn.addEventListener('touchcancel', stopBreak, { passive: false });

    this.container.appendChild(btn);
  }

  private triggerJump() {
    this.player.keys['Space'] = true;
    setTimeout(() => { this.player.keys['Space'] = false; }, 150);
  }

  private boundTouchStart!: (e: TouchEvent) => void;
  private boundTouchMove!: (e: TouchEvent) => void;
  private boundTouchEnd!: (e: TouchEvent) => void;

  private isInsideUIOverlay(el: EventTarget | null): boolean {
    if (!(el instanceof HTMLElement)) return false;
    return !!el.closest('#death-screen, #sleep-overlay, #crafting-screen, #help-screen, #player-list-overlay, #chat-input, #world-selector');
  }

  private bindTouchEvents() {
    this.boundTouchStart = (e: TouchEvent) => {
      // Don't intercept touches on UI overlays — let click events fire normally
      if (this.isInsideUIOverlay(e.target)) return;

      for (let i = 0; i < e.changedTouches.length; i++) {
        const touch = e.changedTouches[i];
        const screenMid = window.innerWidth / 2;

        if (touch.clientX < screenMid && this.joystickTouchId === null) {
          e.preventDefault();
          this.joystickTouchId = touch.identifier;
          this.joystickCenterX = touch.clientX;
          this.joystickCenterY = touch.clientY;
          this.joystickBase.classList.add('active');
        } else if (touch.clientX >= screenMid && this.lookTouchId === null) {
          e.preventDefault();
          this.lookTouchId = touch.identifier;
          this.lookCenterX = touch.clientX;
          this.lookCenterY = touch.clientY;
          this.lookDeltaX = 0;
          this.lookDeltaY = 0;
          this.lookBase.classList.add('active');
        }
      }
    };

    this.boundTouchMove = (e: TouchEvent) => {
      if (this.isInsideUIOverlay(e.target)) return;

      for (let i = 0; i < e.changedTouches.length; i++) {
        const touch = e.changedTouches[i];

        if (touch.identifier === this.joystickTouchId) {
          e.preventDefault();
          this.updateMoveJoystick(touch.clientX, touch.clientY);
        } else if (touch.identifier === this.lookTouchId) {
          e.preventDefault();
          this.updateLookJoystick(touch.clientX, touch.clientY);
        }
      }
    };

    this.boundTouchEnd = (e: TouchEvent) => {
      for (let i = 0; i < e.changedTouches.length; i++) {
        const touch = e.changedTouches[i];

        if (touch.identifier === this.joystickTouchId) {
          this.joystickTouchId = null;
          this.moveX = 0;
          this.moveZ = 0;
          this.isSprinting = false;
          this.joystickKnob.style.transform = 'translate(-50%, -50%)';
          this.joystickBase.classList.remove('active');
        } else if (touch.identifier === this.lookTouchId) {
          this.lookTouchId = null;
          this.lookDeltaX = 0;
          this.lookDeltaY = 0;
          this.lookKnob.style.transform = 'translate(-50%, -50%)';
          this.lookBase.classList.remove('active');
        }
      }
    };

    document.addEventListener('touchstart', this.boundTouchStart, { passive: false });
    document.addEventListener('touchmove', this.boundTouchMove, { passive: false });
    document.addEventListener('touchend', this.boundTouchEnd, { passive: false });
    document.addEventListener('touchcancel', this.boundTouchEnd, { passive: false });
  }

  private updateMoveJoystick(touchX: number, touchY: number) {
    const dx = touchX - this.joystickCenterX;
    const dy = touchY - this.joystickCenterY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const maxDist = 50;

    const clampedDist = Math.min(dist, maxDist);
    const angle = Math.atan2(dy, dx);
    const clampedX = Math.cos(angle) * clampedDist;
    const clampedY = Math.sin(angle) * clampedDist;

    this.joystickKnob.style.transform = `translate(calc(-50% + ${clampedX}px), calc(-50% + ${clampedY}px))`;

    const normX = clampedX / maxDist;
    const normY = clampedY / maxDist;

    this.moveX = normX;
    this.moveZ = -normY;

    this.isSprinting = dist > maxDist * 0.85;
  }

  private updateLookJoystick(touchX: number, touchY: number) {
    const dx = touchX - this.lookCenterX;
    const dy = touchY - this.lookCenterY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const maxDist = 50;

    const clampedDist = Math.min(dist, maxDist);
    const angle = Math.atan2(dy, dx);
    const clampedX = Math.cos(angle) * clampedDist;
    const clampedY = Math.sin(angle) * clampedDist;

    this.lookKnob.style.transform = `translate(calc(-50% + ${clampedX}px), calc(-50% + ${clampedY}px))`;

    // Normalized -1..1 used in the update loop for continuous camera rotation
    this.lookDeltaX = clampedX / maxDist;
    this.lookDeltaY = clampedY / maxDist;
  }

  private bindHotbarTap() {
    const hotbar = document.getElementById('hotbar');
    if (!hotbar) return;
    hotbar.style.pointerEvents = 'auto';
    hotbar.addEventListener('touchstart', (e) => {
      e.stopPropagation(); // Don't let it reach look zone
      const target = (e.target as HTMLElement).closest('.hotbar-slot') as HTMLElement | null;
      if (target) {
        const index = Array.from(hotbar.children).indexOf(target);
        if (index >= 0) this.callbacks.onSelectSlot(index);
      }
    }, { passive: false });
  }

  private startUpdateLoop() {
    const LOOK_SPEED = 12; // radians per second at full tilt
    let lastTime = performance.now();

    const update = () => {
      const now = performance.now();
      const dt = (now - lastTime) / 1000;
      lastTime = now;

      // Feed move joystick into player keys
      const deadzone = 0.15;
      this.player.keys['KeyW'] = this.moveZ > deadzone;
      this.player.keys['KeyS'] = this.moveZ < -deadzone;
      this.player.keys['KeyA'] = this.moveX < -deadzone;
      this.player.keys['KeyD'] = this.moveX > deadzone;
      this.player.sprinting = this.isSprinting;

      // Feed look joystick into camera — continuous rotation while held
      if (Math.abs(this.lookDeltaX) > deadzone || Math.abs(this.lookDeltaY) > deadzone) {
        const lookX = this.lookDeltaX * LOOK_SPEED * dt * 50;
        const lookY = this.lookDeltaY * LOOK_SPEED * dt * 50;
        this.player.onMouseMove(lookX, lookY);
      }

      this.rafId = requestAnimationFrame(update);
    };
    this.rafId = requestAnimationFrame(update);
  }

  dispose() {
    if (this.breakInterval) clearInterval(this.breakInterval);
    cancelAnimationFrame(this.rafId);
    document.removeEventListener('touchstart', this.boundTouchStart);
    document.removeEventListener('touchmove', this.boundTouchMove);
    document.removeEventListener('touchend', this.boundTouchEnd);
    document.removeEventListener('touchcancel', this.boundTouchEnd);
    this.container.remove();
  }
}
